import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import {
  getCharacterById,
  setWeaponToCharacter,
  fetchWeapons,
  addWeaponToCharacter,
  removeWeaponFromCharacter,
  type CharacterInstance
} from './action'
import { applyWeaponInventoryOp, type PendingWeaponOp } from './pendingWeaponOps'

// ✨ Character State Interface
export interface CharacterState {
  currentCharacter: CharacterInstance | null
  weapons: any[]
  isLoading: boolean
  error: string | null
  lastUpdated: Date | null
  /** Last successful `fetchWeapons` wallet/time — inventory page can skip repeat fetches when revisiting. */
  weaponsFetchCache: { walletAddress: string; fetchedAt: number } | null
  /** Equips the player has performed but the server has not acknowledged. See `pendingWeaponOps.ts`. */
  pendingWeaponOps: PendingWeaponOp[]
}

const initialState: CharacterState = {
  currentCharacter: null,
  weapons: [],
  isLoading: false,
  error: null,
  lastUpdated: null,
  weaponsFetchCache: null,
  pendingWeaponOps: [],
}

export const characterSlice = createSlice({
  name: 'character',
  initialState,
  reducers: {
    // ✨ Set current character
    setCurrentCharacter: (state, action: PayloadAction<CharacterInstance | null>) => {
      state.currentCharacter = action.payload
      state.lastUpdated = new Date()
    },

    // ✨ Clear character data
    clearCharacter: (state) => {
      state.currentCharacter = null
      state.error = null
    },

    // ✨ Update character stats locally
    updateCharacterStats: (state, action: PayloadAction<Partial<CharacterInstance['stats']>>) => {
      if (state.currentCharacter) {
        state.currentCharacter.stats = {
          ...state.currentCharacter.stats,
          ...action.payload
        }
        state.lastUpdated = new Date()
      }
    },

    // Removed updateCharacterProgress - campaign progress is now user-level

    // ✨ Update character level and experience
    updateCharacterLevel: (state, action: PayloadAction<{ level: number; experience: number }>) => {
      if (state.currentCharacter) {
        state.currentCharacter.level = action.payload.level
        state.currentCharacter.experience = action.payload.experience
        state.lastUpdated = new Date()
      }
    },

    // ✨ Set weapons
    setWeapons: (state, action: PayloadAction<any[]>) => {
      state.weapons = action.payload
      state.lastUpdated = new Date()
    },

    /* ---------------------------------------------------------------- */
    /* Pending equip intent                                             */
    /* ---------------------------------------------------------------- */
    /** Record the player's intent. The slot selector replays it over server state immediately. */
    enqueueWeaponOp: (state, action: PayloadAction<PendingWeaponOp>) => {
      state.pendingWeaponOps.push(action.payload)
    },
    /**
     * The server acknowledged the operation. Its effect is folded into the inventory list and the
     * op is dropped in the same action, so the list never changes across the two — dropping it
     * alone would un-replay the equip and pop the tile back until the next fetch. The slots are
     * folded by the game slice, which handles this same action.
     *
     * Nothing is read out of the response: a success means "it committed", not "here is the state".
     */
    confirmWeaponOp: (state, action: PayloadAction<PendingWeaponOp>) => {
      const op = action.payload
      if (!state.pendingWeaponOps.some((pending) => pending.id === op.id)) return
      state.pendingWeaponOps = state.pendingWeaponOps.filter((pending) => pending.id !== op.id)
      state.weapons = applyWeaponInventoryOp(state.weapons, op)
    },
    /** The server rejected it. Dropping the op un-replays its effect; server state is untouched. */
    failWeaponOp: (state, action: PayloadAction<{ id: string }>) => {
      state.pendingWeaponOps = state.pendingWeaponOps.filter((op) => op.id !== action.payload.id)
    },

    // ✨ Update weapon character assignment
    updateWeaponAssignment: (state, action: PayloadAction<{ weaponId: string; characterId: string }>) => {
      state.weapons = state.weapons.map(weapon => ({
        ...weapon,
        characterID: weapon._id === action.payload.weaponId 
          ? action.payload.characterId 
          : (weapon.characterID === action.payload.characterId ? '' : weapon.characterID)
      }))
      state.lastUpdated = new Date()
    },
  },
  extraReducers: (builder) => {
    // ✨ Get Character by ID
    builder
      .addCase(getCharacterById.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getCharacterById.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentCharacter = action.payload
        state.lastUpdated = new Date()
      })
      .addCase(getCharacterById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to fetch character'
      })

    // ✨ Set Weapon to Character
    builder
      .addCase(setWeaponToCharacter.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(setWeaponToCharacter.fulfilled, (state) => {
        state.isLoading = false
        // Weapon set successful
      })
      .addCase(setWeaponToCharacter.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to set weapon'
      })

    // ✨ Fetch Weapons
    builder
      .addCase(fetchWeapons.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchWeapons.fulfilled, (state, action) => {
        state.isLoading = false
        state.weapons = action.payload
        state.lastUpdated = new Date()
        state.weaponsFetchCache = {
          walletAddress: action.meta.arg.walletAddress,
          fetchedAt: Date.now(),
        }
      })
      .addCase(fetchWeapons.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to fetch weapons'
      })

    // ✨ Add Weapon to Character
    builder
      .addCase(addWeaponToCharacter.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(addWeaponToCharacter.fulfilled, (state) => {
        state.isLoading = false
        state.lastUpdated = new Date()
      })
      .addCase(addWeaponToCharacter.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to add weapon'
      })

    // ✨ Remove Weapon from Character
    builder
      .addCase(removeWeaponFromCharacter.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(removeWeaponFromCharacter.fulfilled, (state) => {
        state.isLoading = false
        state.lastUpdated = new Date()
      })
      .addCase(removeWeaponFromCharacter.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to remove weapon'
      })
  },
})

export const {
  setCurrentCharacter,
  clearCharacter,
  updateCharacterStats,
  updateCharacterLevel,
  setWeapons,
  updateWeaponAssignment,
  enqueueWeaponOp,
  confirmWeaponOp,
  failWeaponOp
} = characterSlice.actions
export default characterSlice.reducer
